// apps/admin/src/hooks/useAdminAuditLog.ts
import { useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

interface LogActionParams {
  action: string;
  companyId?: string;
  companyName?: string;
  resource: string;
  resourceName?: string;
  metadata?: Record<string, any>;
  severity?: 'INFO' | 'WARNING' | 'CRITICAL';
}

/**
 * Hook for logging admin actions to the global audit log
 *
 * Google Pattern: Client-side audit logging for compliance
 * Privacy Act APP 13: Track who accessed what company data
 *
 * Usage:
 * const { logAction } = useAdminAuditLog();
 * await logAction({
 *   action: 'ADMIN_COMPANY_VIEWED',
 *   companyId: '123',
 *   companyName: 'ACME Corp',
 *   resource: 'companies/123',
 *   resourceName: 'ACME Corp'
 * });
 */
export const useAdminAuditLog = () => {
  const logAction = useCallback(async (params: LogActionParams): Promise<void> => {
    try {
      const logAdminAction = httpsCallable(functions, 'logAdminAction');
      await logAdminAction(params);
    } catch (error) {
      // Don't throw errors to avoid breaking the UI
      // Audit logging is important but shouldn't block user actions
      console.error('[useAdminAuditLog] Failed to log action:', error);
    }
  }, []);

  return { logAction };
};
